import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonFunctionArgs = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Mastering Function Arguments</h1>
                <p className="subtitle">From basic positional args to advanced packing, unpacking, and API design patterns.</p>
            </div>

            <div className="content-section">
                <h2>1. The Basics: Positional vs Keyword</h2>
                <p>
                    In Python, you can pass arguments to a function in two ways. Understanding the difference is crucial for reading modern codebases.
                </p>
                <ul>
                    <li><strong>Positional Arguments:</strong> The order matters. The 1st value goes to the 1st parameter.</li>
                    <li><strong>Keyword Arguments:</strong> The name matters. Order is ignored (if names are explicit).</li>
                </ul>

                <CodeBlock language="python" code={`def create_profile(name, age, city):
    print(f"{name} is {age} years old from {city}")

# 1. Positional (Order matters)
create_profile("Alice", 30, "New York")

# 2. Keyword (Order doesn't matter)
create_profile(age=30, city="New York", name="Alice")

# 3. Mixed (Positional MUST come first!)
create_profile("Alice", city="London", age=25)`} />
            </div>

            <div className="content-section">
                <h2>2. Theory: How Python Passes Arguments</h2>
                <ProTip><p><strong>The Golden Rule:</strong> Python is neither "Pass by Value" nor "Pass by Reference". It is <strong>"Pass by Object Reference"</strong> (or Pass by Assignment).</p></ProTip>
                <p>
                    This is a major source of confusion for C++ or Java developers.
                </p>
                <ul>
                    <li><strong>Immutable Objects (int, str):</strong> Act like "By Value". If you change them inside the function, the outsider doesn't see it (because you actually created a <em>new</em> object).</li>
                    <li><strong>Mutable Objects (list, dict):</strong> Act like "By Reference". If you Modify them (append, update), the outsider sees it.</li>
                </ul>
                <CodeBlock language="python" code={`def modify_them(n, l):
    n = 100        # Rebinds local 'n' to a new integer. Outer 'n' is unchanged.
    l.append(100)  # Modifies the actual list object. Outer 'l' IS changed.

a = 10
b = [1, 2]

modify_them(a, b)

print(a) # 10 (Unchanged)
print(b) # [1, 2, 100] (Changed!)`} />
            </div>

            <div className="content-section">
                <h2>3. CRITICAL PITFALL: Mutable Default Arguments</h2>
                <ProTip><p>
                        <strong>Stop. Read this carefully.</strong> This is the #1 bug in Python interviews and junior codebases.
                    </p></ProTip>
                <p>
                    Beginners often write functions like `def add_item(item, list=[])`. They expect a <em>new</em> empty list every time the function is called.
                    <strong>They are wrong.</strong>
                </p>
                <p>
                    In Python, default argument values are evaluated <strong>only once</strong>, when the function is <em>defined</em>, not when it is <em>called</em>.
                    If that value is mutable (like a list or dict), the <strong>same object</strong> is shared across all calls.
                </p>

                <h3>The Bug in Action</h3>
                <CodeBlock language="python" code={`#  BAD: The list is created once at definition time
def add_student(name, roster=[]):
    roster.append(name)
    return roster

# Usage
class_a = add_student("Alice")
print(class_a) # ['Alice'] (Looks fine...)

class_b = add_student("Bob")
print(class_b) 
# EXPECTED: ['Bob']
# ACTUAL:   ['Alice', 'Bob'] 

# Why? 'roster' is the SAME list object in memory for both calls!`} />

                <h3>The Fix: The "None" Sentinel</h3>
                <p>
                    To fix this, default to `None` (which is immutable) and create the new list inside the function body.
                </p>
                <CodeBlock language="python" code={`# GOOD: New list created at runtime
def add_student(name, roster=None):
    if roster is None:
        roster = [] # New list created NOW
    
    roster.append(name)
    return roster

print(add_student("Alice")) # ['Alice']
print(add_student("Bob"))   # ['Bob'] (Correct!)`} />
            </div>

            <div className="content-section">
                <h2>2. Flexible Arguments: *args and **kwargs</h2>
                <p>
                    Sometimes you don't know how many arguments a user will pass.
                    Python handles this with <strong>Packing</strong> operators.
                </p>

                <h3>Positional Packing (*args)</h3>
                <p>
                    The `*` operator (splat) collects all extra positional arguments into a <strong>tuple</strong>.
                    Note: The name `args` is convention, but you could call it `*bananas`. (Please don't).
                </p>
                <CodeBlock language="python" code={`def sum_all(*numbers):
    print(type(numbers)) # <class 'tuple'>
    total = 0
    for n in numbers:
        total += n
    return total

print(sum_all(1, 2, 3))       # 6
print(sum_all(10, 20, 30, 40)) # 100`} />

                <h3>Keyword Packing (**kwargs)</h3>
                <p>
                    The `**` operator (double splat) collects all extra keyword arguments into a <strong>dictionary</strong>.
                </p>
                <CodeBlock language="python" code={`def build_html_tag(tag, **attributes):
    # 'tag' is a normal argument.
    # 'attributes' is a dictionary of the rest.
    
    attr_str = ""
    for key, value in attributes.items():
        attr_str += f' {key}="{value}"'
    
    return f"<{tag}{attr_str}></{tag}>"

print(build_html_tag("a", href="google.com", target="_blank"))
# Output: <a href="google.com" target="_blank"></a>

print(build_html_tag("div", id="main", className="container"))
# Output: <div id="main" className="container"></div>`} />
            </div>

            <div className="content-section">
                <h2>3. Unpacking: The Inverse Operation</h2>
                <p>
                    We just saw how to pack arguments <em>inside</em> a function definition.
                    But you can also use `*` and `**` when <strong>calling</strong> a function to unpack collections.
                </p>

                <CodeBlock language="python" code={`def register_user(name, email, age):
    print(f"User: {name}, Email: {email}, Age: {age}")

user_data_list = ["Alice", "alice@example.com", 25]

#  Tedious
# register_user(user_data_list[0], user_data_list[1], user_data_list[2])

# Efficient (Unpacking)
register_user(*user_data_list) 
# Python expands this to: register_user("Alice", "alice@example.com", 25)

user_data_dict = {
    "age": 30,
    "email": "bob@example.com",
    "name": "Bob" 
}

# Keyword Unpacking (Keys must match argument names!)
register_user(**user_data_dict)`} />
            </div>

            <div className="content-section">
                <h2>Deep Dive: Memory & Performance</h2>
                <p>
                    You might think `*args` is just syntactic sugar, but it has real memory implications.
                    When you call a function with `func(1, 2, 3)` and it accepts `*args`, Python must:
                </p>
                <ol>
                    <li>Allocate a new <strong>tuple</strong> object in memory.</li>
                    <li>Copy the pointers of `1`, `2`, and `3` into that tuple.</li>
                    <li>Pass that tuple to the function frame.</li>
                </ol>
                <p>
                    For 3 items, this is negligible. But if you unpack a list of 1 million items (`func(*huge_list)`), Python has to iterate the entire list and build a new tuple copy.
                    <strong>Pro Tip:</strong> If performance is critical and data is huge, pass the list directly (`func(my_list)`) instead of unpacking it.
                </p>

                <h3>Language Comparison</h3>
                <p>
                    How does Python compare to others?
                </p>
                <ul>
                    <li><strong>Java (`String... args`):</strong> Similar. Creates an Array behind the scenes. Statically typed.</li>
                    <li><strong>JavaScript (`...args`):</strong> Creates a genuine Array (mutable). Python's `args` are always immutable Tuples.</li>
                    <li><strong>C:</strong> Uses `va_list` macros (very manual and dangerous). Python handles safety for you.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>The "Wrapper" Pattern: Forwarding Arguments</h2>
                <p>
                    The most common use of `*args` and `**kwargs` isn't to use the data yourselfit's to pass it to someone else.
                    This is called <strong>Argument Forwarding</strong>.
                </p>
                <p>
                    Imagine you are writing a subclass. You want to override `__init__` to add one property, but keep the rest the same.
                    Do you copy-paste the parent's arguments? No!
                </p>

                <CodeBlock language="python" code={`class Parent:
    def __init__(self, name, age, city="Unknown"):
        self.name = name
        self.age = age
        self.city = city

class Child(Parent):
    def __init__(self, school, *args, **kwargs):
        # 1. Capture our specific argument
        self.school = school
        
        # 2. Forward EVERYTHING else to Parent
        # We don't care if Parent changes its signature later!
        super().__init__(*args, **kwargs)

# Usage
kid = Child("Elementary", "Alice", 10, city="New York")
print(kid.school) # Elementary
print(kid.name)   # Alice
print(kid.city)   # New York`} />
                <ProTip><p><strong>Gold Standard:</strong> This makes your code "future-proof". If `Parent` adds a new argument tomorrow, `Child` keeps working automatically.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. Modern Python: Enforcing Argument Types</h2>
                <p>
                    As libraries grow, maintaining backward compatibility is hard. Python 3.8 introduced special syntax to <em>force</em> how users pass arguments.
                    This is done using the <code>/</code> and <code>*</code> separators.
                </p>

                <h3>Positional-Only Arguments (The `/`)</h3>
                <p>
                    Arguments <em>before</em> the `/` <strong>cannot</strong> be passed by keyword. They must be positional.
                    This is useful for functions where the argument name is meaningless (like `len(obj)` or `pow(x, y)`).
                </p>
                <CodeBlock language="python" code={`def fast_math(x, y, /):
    return x + y

print(fast_math(10, 20)) # Works

# print(fast_math(x=10, y=20)) 
#  TypeError: fast_math() got some positional-only arguments passed as keyword arguments: 'x', 'y'`} />

                <h3>Keyword-Only Arguments (The `*`)</h3>
                <p>
                    Arguments <em>after</em> the `*` <strong>must</strong> be passed by keyword. They cannot be positional.
                    This is excellent for configurations or dangerous flags (like `force=True`) that you don't want users to set accidentally.
                </p>
                <CodeBlock language="python" code={`def delete_user(user_id, *, force=False):
    if force:
        print(f"User {user_id} deleted forever!")
    else:
        print(f"User {user_id} moved to trash.")

# delete_user(123, True) 
#  TypeError: delete_user() takes 1 positional argument but 2 were given

delete_user(123, force=True) # Works`} />

                <h3>The Grand Unified Theory</h3>
                <p>You can combine them all (but please don't unless you're writing a library):</p>
                <CodeBlock language="python" code={`def monster(a, b, /, c, d, *, e, f):
    print(a, b, c, d, e, f)

# a, b: Positional ONLY
# c, d: Either
# e, f: Keyword ONLY`} />
            </div>

            <div className="content-section">
                <h2>5. Type Hinting Flexible Arguments</h2>
                <p>
                    In modern Python (3.10+), we use standard typing to hint `*args` and `**kwargs`.
                </p>
                <CodeBlock language="python" code={`from typing import Any

# For *args, specify the type of a SINGLE item
def mean(*values: float) -&gt; float:
    return sum(values) / len(values)

# For **kwargs, specify the type of the VALUES
def build_config(**settings: str) -&gt; dict[str, str]:
    return settings

# Note: We don't type hint the tuple/dict container itself, 
# just the contents!`} />
            </div>

            <div className="content-section">
                <h2>Real World Project: The Validated Configurator</h2>
                <p>
                    A common pattern in Data Science and Web Development is passing a dynamic configuration dictionary.
                    Let's build a class that takes generic `**kwargs` but validates them strictly.
                </p>

                <CodeBlock language="python" code={`class DatabaseConnection:
    # We use **options to accept any driver-specific settings
    def __init__(self, host="localhost", port=5432, **options):
        self.host = host
        self.port = port
        self.options = options # Store the rest specific to the driver
        
        self.validate()
        
    def validate(self):
        # Enforce that certain "extra" options exists
        if "timeout" not in self.options:
            print(" access: Defaulting timeout to 30s")
            self.options["timeout"] = 30
            
    def connect(self):
        print(f" Connecting to {self.host}:{self.port}")
        for k, v in self.options.items():
            print(f"   -&gt; Setting {k} = {v}")

# Usage
# 1. Standard
db = DatabaseConnection()
db.connect()

# 2. Custom with Extra Options
pg_db = DatabaseConnection(
    host="192.168.1.5", 
    ssl_mode="require",  # Captured in **options
    timeout=60          # Captured in **options
)
pg_db.connect()`} />
                <ProTip><p><strong>Pattern:</strong> This "Arguments Tunnel" pattern allows your class to support future features without changing the method signature. The `**options` dict just catches everything new.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Under the Hood: Argument Binding</h2>
                <p>
                    When you call a function, Python's interpreter goes through a strict "Binding" phase to map your values to variables.
                    Understanding this order helps you debug `TypeError`.
                </p>
                <ol>
                    <li><strong>Positional:</strong> Slots are filled left-to-right.</li>
                    <li><strong>Keywords:</strong> Named arguments look for empty slots. If a slot is already filled by positional, ERROR.</li>
                    <li><strong>Defaults:</strong> Any empty slots remaining get their default values.</li>
                    <li><strong>*args:</strong> Any <em>extra</em> positional values go here (if `*args` exists).</li>
                    <li><strong>**kwargs:</strong> Any <em>extra</em> keyword values go here (if `**kwargs` exists).</li>
                </ol>
                <p>
                    This complexity is why "Keyword-Only" arguments are so valuablethey skip the ambiguous "Positional" phase entirely.
                </p>
            </div>

            <div className="content-section">
                <h2>6. Debugging Common `TypeError`s</h2>
                <p>
                    Argument errors are the most common runtime crashes. Here is how to decode them.
                </p>

                <h3>1. "positional argument follows keyword argument"</h3>
                <CodeBlock language="python" code={`#  Bad
# func(a=10, 20)
# SyntaxError: positional argument follows keyword argument`} />
                <p>
                    <strong>Fix:</strong> Once you start using keywords (`a=10`), <em>every argument after that</em> must also be a keyword.
                </p>

                <h3>2. "multiple values for argument 'x'"</h3>
                <CodeBlock language="python" code={`def add(x, y): return x + y

#  Bad
# add(10, x=20) 
# TypeError: add() got multiple values for argument 'x'`} />
                <p>
                    <strong>Why?</strong> Python assigns the first positional (`10`) to `x`. Then it sees `x=20` and tries to assign to `x` again.
                    <strong>Fix:</strong> Don't mix positional and keyword for the <em>same</em> variable.
                </p>

                <h3>3. "unexpected keyword argument"</h3>
                <CodeBlock language="python" code={`def user(name): pass

#  Bad
# user(name="Bob", age=30)
# TypeError: user() got an unexpected keyword argument 'age'`} />
                <p>
                    <strong>Fix:</strong> The function didn't expect `age`. Add `**kwargs` to the definition if you want to accept extra junk.
                </p>
            </div>

            <div className="content-section">
                <h2>7. Refactoring Case Study: From Messy to Clean</h2>
                <p>
                    Let's refactor a messy API client function that suffers from "Argument Creep".
                </p>

                <h3>Before: The "Positional" Hell</h3>
                <CodeBlock language="python" code={`#  Hard to read, hard to extend
def fetch_data(url, method, timeout, headers, retry, cache):
    pass

# Usage
fetch_data("api.com", "GET", 30, None, True, False)
# What is 30? What is True? What is False?`} />

                <h3>After: The "Keyword" Heaven</h3>
                <CodeBlock language="python" code={`# Self-documenting and safe
def fetch_data(url, *, method="GET", timeout=30, headers=None, retry=True, cache=False):
    pass

# Usage
fetch_data("api.com", timeout=60, retry=False)
# We skipped arguments we didn't care about!
# We know exactly what '60' and 'False' mean.`} />
                <ProTip><p><strong>Victory:</strong> By forcing keywords (using `*`), we made the call site readable and allowed defaults to handle 80% of the cases.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>6. Introspection: Looking at the Gears</h2>
                <p>
                    Everything in Python is an object, including the function's argument definitions.
                    You can inspect these directly to debug weird behavior.
                </p>

                <h3>Accessing Defaults Directly</h3>
                <p>
                    Remember the "Mutable Default" bug? We can actually <em>see</em> the mistake stored in memory.
                </p>
                <CodeBlock language="python" code={`def bad_func(x=[]):
    pass

print(bad_func.__defaults__) 
# ([],) -&gt; The list exists here!

bad_func() # Modify it...
print(bad_func.__defaults__) 
# (['modified!'],) -&gt; We can see the pollution!`} />

                <h3>The `inspect` Module</h3>
                <p>
                    For professional tools, avoid accessing `__dunder__` attributes. Use the standard `inspect` module.
                </p>
                <CodeBlock language="python" code={`import inspect

def process(a, b=2, *args, **kwargs):
    pass

sig = inspect.signature(process)
print(sig) # (a, b=2, *args, **kwargs)

for name, param in sig.parameters.items():
    print(f"{name}: {param.kind} (Default: {param.default})")`} />

                <h3>Pro Tip: Argument Binding</h3>
                <p>
                    The most powerful feature of `inspect` is <code>bind()</code>.
                    It simulates a function call <em>without actually calling the function</em>.
                    This is how frameworks like Django or FastAPI validate your inputs before running your view.
                </p>
                <CodeBlock language="python" code={`def View(request, user_id):
    pass

sig = inspect.signature(View)

# Simulate a call:
try:
    # We forgot 'user_id'!
    bound = sig.bind(request={'url': '/home'}) 
except TypeError as e:
    print(f"Validation Error: {e}") 
    # Output: Validation Error: missing a required argument: 'user_id'

# Correct call simulation:
bound = sig.bind({'url': '/home'}, 123)
print(bound.arguments)
# OrderedDict([('request', {'url': '/home'}), ('user_id', 123)])`} />
            </div>

            <div className="content-section">
                <h2>7. Anti-Patterns: When Flexibility Bites</h2>
                <p>
                    Just because you <em>can</em> accept `**kwargs` doesn't mean you <em>should</em>.
                </p>

                <h3>The "Mystery Meat" API</h3>
                <p>
                    Imagine you inherit this code:
                </p>
                <CodeBlock language="python" code={`def connect(**params):
    # What goes in here?
    # Host? IP? Username? User_Name?
    pass`} />
                <p>
                    This is unreadable. The user has to read the source code to know what to pass.
                    <strong>Best Practice:</strong> Explicit is better than implicit. Only use `**kwargs` if you are truly forwarding data to something else (like a database driver or parent class).
                </p>
            </div>

            <div className="content-section">
                <h2>8. Extended Iterable Unpacking</h2>
                <p>
                    Unpacking isn't just for function calls. It works in assignment too!
                    You can grab the "head" and "tail" of a list elegantly.
                </p>
                <CodeBlock language="python" code={`data = [1, 2, 3, 4, 5, 6]

# Grab first, last, and middle
first, *middle, last = data

print(first)   # 1
print(middle)  # [2, 3, 4, 5]
print(last)    # 6

# Grab first two
a, b, *rest = data
print(rest)    # [3, 4, 5, 6]`} />
            </div>

            <div className="content-section">
                <h2>Summary: Best Practices Checklist</h2>
                <ProTip><ul>
                        <li><strong>Standard First:</strong> Always put standard usage first. Complex features (`*args`) should be opt-in.</li>
                        <li><strong>Explicit {">"} Implicit:</strong> Use Keyword-Only arguments for boolean flags (`force=True`).</li>
                        <li><strong>Avoid Mutables:</strong> Never use empty lists/dicts as defaults. Use `None`.</li>
                        <li><strong>Document:</strong> If you use `**kwargs`, write a docstring listing exactly what keys are expected.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>9. Interview Prep: The Ordering Rule</h2>
                <ProTip><p><strong>Question:</strong> Why does `*args` always come before `**kwargs`?</p></ProTip>
                <p>
                    <strong>Answer:</strong> Because Positional arguments are rigidly ordered, while Keyword arguments are flexible.
                    If you tried `def func(**kwargs, *args)`, Python wouldn't know when the "positional" part starts because keywords can appear anywhere in the call.
                </p>
                <CodeBlock language="python" code={`# The Law of Order:
# 1. Standard Positional  (a, b)
# 2. Standard Default     (c=10)
# 3. Positional Packing   (*args)
# 4. Keyword-Only         (*, d, e)
# 5. Keyword Packing      (**kwargs)`} />
            </div>

        </div>
    );
};

export default PythonFunctionArgs;


